/**
 * Central registry of all Zustand persist localStorage keys.
 *
 * IMPORTANT: Every store that uses Zustand `persist` middleware MUST register
 * its key here. This registry is used by:
 * - `clearAllPersistedStores()` — wipe all persisted state on sign-out
 * - `clearWorkspaceScopedPersistedStores()` — wipe workspace-scoped data on switch
 *
 * Keys are grouped by scope:
 * - GLOBAL: user-level prefs preserved across workspace switches (UI, navigation)
 * - WORKSPACE: data specific to a workspace context (chat, sessions, search)
 *
 * When adding a new persisted store, add its key here with the correct scope.
 */

/** Keys for stores containing workspace-scoped data. */
export const WORKSPACE_SCOPED_PERSIST_KEYS = [
  'virza-chat-store',
  'virza-sessions',
  'virza-feedback',
  'serenoty-search',
  'virza-research-intelligence',
  'virza-collection-detail',
] as const;

/** Keys for stores containing global (cross-workspace) user preferences. */
export const GLOBAL_PERSIST_KEYS = [
  'virza-workspace',
  'virza-onboarding',
  'virza-ui',
  'virza-navigation',
  'virza-notification-prefs',
  'virza-intro-dismissed',
] as const;

/** Every persisted store key — used for full wipe on sign-out. */
export const ALL_PERSIST_KEYS: readonly string[] = [
  ...WORKSPACE_SCOPED_PERSIST_KEYS,
  ...GLOBAL_PERSIST_KEYS,
];

/**
 * Remove all persisted Zustand state from localStorage and clear the
 * workspace routing cookie. Call on sign-out to prevent data leaking
 * to the next user — both localStorage stores AND the virza-ws-id
 * cookie must be wiped to avoid stale workspace redirects on next login.
 */
export function clearAllPersistedStores(): void {
  if (typeof window === 'undefined') return;
  for (const key of ALL_PERSIST_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable — non-fatal
    }
  }
  // Clear the workspace routing cookie — edge middleware and auth
  // callback read this to decide the post-login redirect target.
  // Without clearing it, the next login inherits the previous user's
  // last workspace, causing a flash-redirect to the wrong workspace.
  try {
    document.cookie = 'virza-ws-id=; path=/; max-age=0';
  } catch {
    // Non-fatal — cookie may already be absent
  }
}

/**
 * Remove workspace-scoped persisted state from localStorage.
 * Call on workspace switch to prevent cross-workspace data leakage.
 */
export function clearWorkspaceScopedPersistedStores(): void {
  if (typeof window === 'undefined') return;
  for (const key of WORKSPACE_SCOPED_PERSIST_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable — non-fatal
    }
  }
}
